var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * CZNA-407 — the rows of the help-text editor: one per help message, described in words
 * a methodologist recognises instead of by its message id.
 *
 * The id (`derivationRules.help.minFrequency`) is never shown — persona Jana
 * (`docs/personas/nk-metodicka.md`) reads it as an error. Instead each row says WHERE the
 * text appears (`AREAS`, by id prefix) and, when the screen has one, the LABEL of the field
 * or column the help belongs to, found among the sibling messages (`labelCandidates`).
 */
import { HELP_MESSAGE_PREFIX, isHelpMessageId } from '../utils/helpTextOverrides';
var SIMPLE_ARGUMENT = /\{\s*[^\s,{}]+\s*\}/g;
/**
 * The wording as the list shows it: a `{name}` placeholder becomes "…". Users see a number
 * there, and the name (`{awaitingNk}`) is code that persona Jana reads as an error. The
 * editor keeps the source, because a rewording must keep the placeholder.
 */
export var listWording = function (text) { return text.replace(SIMPLE_ARGUMENT, '…'); };
/** Most specific prefix first: the first match wins. */
var AREAS = [
    { prefix: 'nav.inbox.', area: 'screenInbox' },
    { prefix: 'nav.reviewQueue.', area: 'screenReviewQueue' },
    { prefix: 'nav.authorities.', area: 'screenAuthorities' },
    { prefix: 'nav.headings.', area: 'screenHeadings' },
    { prefix: 'nav.vocabularies.', area: 'screenVocabularies' },
    { prefix: 'nav.oaiHarvest.', area: 'screenOaiHarvest' },
    { prefix: 'derivationRules.createType.', area: 'derivationCreateType' },
    { prefix: 'derivationRules.overviewHelp.', area: 'derivationOverview' },
    { prefix: 'derivationRules.', area: 'derivationRule' },
    { prefix: 'review.queue.column.', area: 'reviewQueue' },
    { prefix: 'index.column.', area: 'headingsColumns' },
    { prefix: 'semanticCatalog.map.', area: 'ontologyMap' },
    { prefix: 'semanticCatalog.', area: 'ontology' },
    { prefix: 'settings.forms.templates.', area: 'formTemplates' },
    { prefix: 'settings.forms.visual.', area: 'formFieldProperties' },
    { prefix: 'validationRules.mvlHelp.', area: 'mvlSyntax' },
    { prefix: 'corpusQuality.checks.help.', area: 'corpusQuality' },
    { prefix: 'vocabularyTypes.', area: 'vocabularyTypes' },
];
var OTHER_AREA = 'other';
export var AREA_IDS = __spreadArray(__spreadArray([], AREAS.map(function (a) { return a.area; }), true), [OTHER_AREA], false);
/** The `guidanceTexts.area.*` key naming where `id` (prefixed) is shown. */
export var areaOf = function (id) {
    var _a, _b;
    var bare = id.substring(HELP_MESSAGE_PREFIX.length);
    return (_b = (_a = AREAS.find(function (a) { return bare.startsWith(a.prefix); })) === null || _a === void 0 ? void 0 : _a.area) !== null && _b !== void 0 ? _b : OTHER_AREA;
};
/**
 * Message ids that may hold the visible label of the control `id` explains, in the order
 * they are tried. Two shapes exist in the translations: `X.help` (the label is `X` or
 * `X.label`) and `A.help.B` (the label is `B` somewhere under `A`). A corpus check's texts
 * (`A.help.K01.meaning`, `.fix`) are labelled by the check's name (`A.help.K01.name`) —
 * the name itself is a row of its own, with no label (CZNA-1407).
 */
var labelCandidates = function (bare) {
    if (bare.endsWith('.help')) {
        var owner = bare.substring(0, bare.length - '.help'.length);
        return [owner, "".concat(owner, ".label")];
    }
    var at = bare.indexOf('.help.');
    if (at === -1) {
        return [];
    }
    var a = bare.substring(0, at);
    var b = bare.substring(at + '.help.'.length);
    var part = b.lastIndexOf('.');
    if (part !== -1 && /^(meaning|fix)$/.test(b.substring(part + 1))) {
        return ["".concat(a, ".help.").concat(b.substring(0, part), ".name")];
    }
    return ["".concat(a, ".").concat(b), "".concat(a, ".field.").concat(b), "".concat(a, ".label.").concat(b), "".concat(a, ".").concat(b, ".label"), "".concat(a, ".column.").concat(b), "".concat(a, ".form.").concat(b), "".concat(a, ".form.").concat(b, ".label")];
};
/**
 * One row per help message in `baseMessages`, in the order of the translation file grouped
 * by area. Non-string messages (a pre-compiled AST) are skipped: there is nothing to show
 * as the default, and the override layer cannot validate against them either.
 */
export var buildHelpTextRows = function (baseMessages, stored) {
    var rows = Object.entries(baseMessages)
        .filter(function (_a) {
        var id = _a[0], text = _a[1];
        return isHelpMessageId(id) && typeof text === 'string';
    })
        .map(function (_a) {
        var id = _a[0], text = _a[1];
        var bare = id.substring(HELP_MESSAGE_PREFIX.length);
        var labelId = labelCandidates(bare)
            .map(function (candidate) { return HELP_MESSAGE_PREFIX + candidate; })
            .find(function (candidate) { return typeof baseMessages[candidate] === 'string'; });
        var storedText = stored[id];
        return {
            id: id,
            area: areaOf(id),
            label: labelId ? baseMessages[labelId] : null,
            defaultText: text,
            stored: typeof storedText === 'string' ? storedText : undefined,
        };
    });
    var order = new Map(AREA_IDS.map(function (area, i) { return [area, i]; }));
    // Array.prototype.sort is stable, so the file order survives within an area.
    return rows.sort(function (x, y) { var _a, _b; return ((_a = order.get(x.area)) !== null && _a !== void 0 ? _a : 0) - ((_b = order.get(y.area)) !== null && _b !== void 0 ? _b : 0); });
};
